type Difficulty = 'Easy' | 'Medium' | 'Hard';

interface Problem {
  id: string;
  title: string;
  difficulty: Difficulty;
  description: string;
  examples: Array<{
    input: string;
    output: string;
    explanation?: string;
  }>;
  boilerplateCode: Record<string, string>;
  starterCode: Record<string, string>;
  testCases: Array<{
    input: string;
    output: string;
  }>;
}

export const linkedListProblems: Problem[] = [
  {
    id: 'linked-lists-1',
    title: 'Reverse Linked List',
    difficulty: 'Easy',
    description: `Given the \`head\` of a singly linked list, reverse the list, and return the reversed list.

**Constraints:**
- The number of nodes in the list is in the range [0, 5000]
- -5000 <= Node.val <= 5000`,
    examples: [
      {
        input: 'head = [1,2,3,4,5]',
        output: '[5,4,3,2,1]',
        explanation: 'The list is reversed.'
      },
      {
        input: 'head = [1,2]',
        output: '[2,1]',
        explanation: 'Simple two-node list reversed.'
      }
    ],
    testCases: [
      {
        input: '[1,2,3,4,5]',
        output: '[5,4,3,2,1]'
      },
      {
        input: '[1,2]',
        output: '[2,1]'
      }
    ],
    starterCode: {
      python: `class ListNode:
    def __init__(self, val=0, next=None):
        self.val = val
        self.next = next

def reverseList(head: ListNode) -> ListNode:
    """Return the reversed linked list"""
    pass`,
      javascript: `/**
 * Definition for singly-linked list.
 * function ListNode(val, next) {
 *     this.val = (val===undefined ? 0 : val)
 *     this.next = (next===undefined ? null : next)
 * }
 */
/**
 * @param {ListNode} head
 * @return {ListNode}
 */
function reverseList(head) {
    // Write your code here
}`,
      cpp: `/**
 * Definition for singly-linked list.
 * struct ListNode {
 *     int val;
 *     ListNode *next;
 *     ListNode() : val(0), next(nullptr) {}
 *     ListNode(int x) : val(x), next(nullptr) {}
 *     ListNode(int x, ListNode *next) : val(x), next(next) {}
 * };
 */
class Solution {
public:
    ListNode* reverseList(ListNode* head) {
        // Write your code here
    }
};`,
      java: `/**
 * Definition for singly-linked list.
 * public class ListNode {
 *     int val;
 *     ListNode next;
 *     ListNode() {}
 *     ListNode(int val) { this.val = val; }
 *     ListNode(int val, ListNode next) { this.val = val; this.next = next; }
 * }
 */
class Solution {
    public ListNode reverseList(ListNode head) {
        // Write your code here
    }
}`
    },
    boilerplateCode: {
      python: `
def arrayToList(arr):
    if not arr: return None
    head = ListNode(arr[0])
    curr = head
    for val in arr[1:]:
        curr.next = ListNode(val)
        curr = curr.next
    return head

def listToArray(head):
    arr = []
    curr = head
    while curr:
        arr.append(curr.val)
        curr = curr.next
    return arr

if __name__ == "__main__":
    # Parse input array
    arr = eval(input().strip())
    
    # Convert to linked list
    head = arrayToList(arr)
    
    # Get result
    result = reverseList(head)
    
    # Convert back to array and print
    print(listToArray(result))`,
      javascript: `
function arrayToList(arr) {
    if (!arr.length) return null;
    const head = new ListNode(arr[0]);
    let curr = head;
    for (let i = 1; i < arr.length; i++) {
        curr.next = new ListNode(arr[i]);
        curr = curr.next;
    }
    return head;
}

function listToArray(head) {
    const arr = [];
    let curr = head;
    while (curr) {
        arr.push(curr.val);
        curr = curr.next;
    }
    return arr;
}

const arr = JSON.parse(require('fs').readFileSync(0, 'utf-8'));
const head = arrayToList(arr);
const result = reverseList(head);
console.log(JSON.stringify(listToArray(result)));`,
      cpp: `
ListNode* arrayToList(vector<int>& arr) {
    if (arr.empty()) return nullptr;
    ListNode* head = new ListNode(arr[0]);
    ListNode* curr = head;
    for (int i = 1; i < arr.size(); i++) {
        curr->next = new ListNode(arr[i]);
        curr = curr->next;
    }
    return head;
}

vector<int> listToArray(ListNode* head) {
    vector<int> arr;
    ListNode* curr = head;
    while (curr) {
        arr.push_back(curr->val);
        curr = curr->next;
    }
    return arr;
}

int main() {
    string line;
    getline(cin, line);
    vector<int> arr = parseArray(line);
    
    Solution solution;
    ListNode* head = arrayToList(arr);
    ListNode* result = solution.reverseList(head);
    
    vector<int> output = listToArray(result);
    cout << formatArray(output) << endl;
    
    // Clean up memory
    while (head) {
        ListNode* temp = head;
        head = head->next;
        delete temp;
    }
    return 0;
}`,
      java: `
public class Main {
    private static ListNode arrayToList(int[] arr) {
        if (arr.length == 0) return null;
        ListNode head = new ListNode(arr[0]);
        ListNode curr = head;
        for (int i = 1; i < arr.length; i++) {
            curr.next = new ListNode(arr[i]);
            curr = curr.next;
        }
        return head;
    }
    
    private static int[] listToArray(ListNode head) {
        List<Integer> list = new ArrayList<>();
        ListNode curr = head;
        while (curr != null) {
            list.add(curr.val);
            curr = curr.next;
        }
        return list.stream().mapToInt(i->i).toArray();
    }
    
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        int[] arr = parseArray(scanner.nextLine());
        
        Solution solution = new Solution();
        ListNode head = arrayToList(arr);
        ListNode result = solution.reverseList(head);
        
        System.out.println(formatArray(listToArray(result)));
    }
}`
    }
  },
  {
    id: 'linked-lists-2',
    title: 'Merge Two Sorted Lists',
    difficulty: 'Easy',
    description: `You are given the heads of two sorted linked lists \`list1\` and \`list2\`.

Merge the two lists into one sorted list. The list should be made by splicing together the nodes of the first two lists.

Return the head of the merged linked list.

**Constraints:**
- The number of nodes in both lists is in the range [0, 50]
- -100 <= Node.val <= 100
- Both list1 and list2 are sorted in non-decreasing order`,
    examples: [
      {
        input: 'list1 = [1,2,4], list2 = [1,3,4]',
        output: '[1,1,2,3,4,4]',
        explanation: 'The two lists are merged in sorted order.'
      },
      {
        input: 'list1 = [], list2 = []',
        output: '[]',
        explanation: 'Both lists being empty, the result is also empty.'
      }
    ],
    testCases: [
      {
        input: '[1,2,4]\n[1,3,4]',
        output: '[1,1,2,3,4,4]'
      },
      {
        input: '[]\n[]',
        output: '[]'
      }
    ],
    starterCode: {
      python: `class ListNode:
    def __init__(self, val=0, next=None):
        self.val = val
        self.next = next

def mergeTwoLists(list1: ListNode, list2: ListNode) -> ListNode:
    """Return the merged sorted linked list"""
    pass`,
      javascript: `/**
 * Definition for singly-linked list.
 * function ListNode(val, next) {
 *     this.val = (val===undefined ? 0 : val)
 *     this.next = (next===undefined ? null : next)
 * }
 */
/**
 * @param {ListNode} list1
 * @param {ListNode} list2
 * @return {ListNode}
 */
function mergeTwoLists(list1, list2) {
    // Write your code here
}`,
      cpp: `/**
 * Definition for singly-linked list.
 * struct ListNode {
 *     int val;
 *     ListNode *next;
 *     ListNode() : val(0), next(nullptr) {}
 *     ListNode(int x) : val(x), next(nullptr) {}
 *     ListNode(int x, ListNode *next) : val(x), next(next) {}
 * };
 */
class Solution {
public:
    ListNode* mergeTwoLists(ListNode* list1, ListNode* list2) {
        // Write your code here
    }
};`,
      java: `/**
 * Definition for singly-linked list.
 * public class ListNode {
 *     int val;
 *     ListNode next;
 *     ListNode() {}
 *     ListNode(int val) { this.val = val; }
 *     ListNode(int val, ListNode next) { this.val = val; this.next = next; }
 * }
 */
class Solution {
    public ListNode mergeTwoLists(ListNode list1, ListNode list2) {
        // Write your code here
    }
}`
    },
    boilerplateCode: {
      python: `
def arrayToList(arr):
    if not arr: return None
    head = ListNode(arr[0])
    curr = head
    for val in arr[1:]:
        curr.next = ListNode(val)
        curr = curr.next
    return head

def listToArray(head):
    arr = []
    curr = head
    while curr:
        arr.append(curr.val)
        curr = curr.next
    return arr

if __name__ == "__main__":
    # Parse input arrays
    arr1 = eval(input().strip())
    arr2 = eval(input().strip())
    
    # Convert to linked lists
    list1 = arrayToList(arr1)
    list2 = arrayToList(arr2)
    
    # Get result
    result = mergeTwoLists(list1, list2)
    
    # Convert back to array and print
    print(listToArray(result))`,
      javascript: `
function arrayToList(arr) {
    if (!arr.length) return null;
    const head = new ListNode(arr[0]);
    let curr = head;
    for (let i = 1; i < arr.length; i++) {
        curr.next = new ListNode(arr[i]);
        curr = curr.next;
    }
    return head;
}

function listToArray(head) {
    const arr = [];
    let curr = head;
    while (curr) {
        arr.push(curr.val);
        curr = curr.next;
    }
    return arr;
}

const input = require('fs').readFileSync(0, 'utf-8').trim().split('\\n');
const arr1 = JSON.parse(input[0]);
const arr2 = JSON.parse(input[1]);
const list1 = arrayToList(arr1);
const list2 = arrayToList(arr2);
const result = mergeTwoLists(list1, list2);
console.log(JSON.stringify(listToArray(result)));`,
      cpp: `
ListNode* arrayToList(vector<int>& arr) {
    if (arr.empty()) return nullptr;
    ListNode* head = new ListNode(arr[0]);
    ListNode* curr = head;
    for (int i = 1; i < arr.size(); i++) {
        curr->next = new ListNode(arr[i]);
        curr = curr->next;
    }
    return head;
}

vector<int> listToArray(ListNode* head) {
    vector<int> arr;
    ListNode* curr = head;
    while (curr) {
        arr.push_back(curr->val);
        curr = curr->next;
    }
    return arr;
}

int main() {
    string line1, line2;
    getline(cin, line1);
    getline(cin, line2);
    vector<int> arr1 = parseArray(line1);
    vector<int> arr2 = parseArray(line2);
    
    Solution solution;
    ListNode* list1 = arrayToList(arr1);
    ListNode* list2 = arrayToList(arr2);
    ListNode* result = solution.mergeTwoLists(list1, list2);
    
    vector<int> output = listToArray(result);
    cout << formatArray(output) << endl;
    
    // Clean up memory
    while (list1) {
        ListNode* temp = list1;
        list1 = list1->next;
        delete temp;
    }
    while (list2) {
        ListNode* temp = list2;
        list2 = list2->next;
        delete temp;
    }
    return 0;
}`,
      java: `
public class Main {
    private static ListNode arrayToList(int[] arr) {
        if (arr.length == 0) return null;
        ListNode head = new ListNode(arr[0]);
        ListNode curr = head;
        for (int i = 1; i < arr.length; i++) {
            curr.next = new ListNode(arr[i]);
            curr = curr.next;
        }
        return head;
    }
    
    private static int[] listToArray(ListNode head) {
        List<Integer> list = new ArrayList<>();
        ListNode curr = head;
        while (curr != null) {
            list.add(curr.val);
            curr = curr.next;
        }
        return list.stream().mapToInt(i->i).toArray();
    }
    
    public static void main(String[] args) {
        Scanner scanner = new Scanner(System.in);
        int[] arr1 = parseArray(scanner.nextLine());
        int[] arr2 = parseArray(scanner.nextLine());
        
        Solution solution = new Solution();
        ListNode list1 = arrayToList(arr1);
        ListNode list2 = arrayToList(arr2);
        ListNode result = solution.mergeTwoLists(list1, list2);
        
        System.out.println(formatArray(listToArray(result)));
    }
}`
    }
  }
];
